/**
 * StickyPricingCTA - Pricing CTA with a collapsible breakdown above the
 * Complete Payment / Start risk assessment button. Two variants:
 *
 * - `sticky` (default): fixed to viewport bottom, mobile only (`md:hidden`),
 *   mounts/unmounts based on `isVisible` (typically when the inline CTA has
 *   scrolled out of view).
 * - `inline`: rendered in normal document flow inside the payment card. Always
 *   visible. Used on both mobile (above the fold) and desktop (replaces the
 *   plain submit button so the same expand/collapse treatment applies).
 *
 * Three pricing states are driven entirely by the price props:
 *
 * | State        | Header label      | Right                | Breakdown                                                  |
 * | ------------ | ----------------- | -------------------- | ---------------------------------------------------------- |
 * | Pre-zip      | "Subtotal"        | $subtotal            | Membership $X / Tax "Enter Zipcode" / Total $X             |
 * | Post-zip     | "Total due today" | $total               | Membership $X / Tax $Y / Total $X+$Y                       |
 * | Post-coupon  | "Total due today" | $total (discounted)  | Membership [X% OFF] strikethrough sub + recurring / Tax /  |
 * |              |                   |                      | Total                                                      |
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface StickyPricingCTAProps {
  /** Subtotal in cents (pre-discount, pre-tax). undefined until price loads. */
  subtotalCents: number | undefined;
  /**
   * Final recurring price in cents (post-discount). When equal to
   * `subtotalCents` (or undefined), no discount line treatment is shown.
   */
  recurringCents: number | undefined;
  /**
   * Tax in cents. undefined => sticky bar shows the "Subtotal" label and
   * renders "Enter Zipcode" in the tax row.
   */
  taxCents: number | undefined;
  /** Total in cents (recurring + tax). undefined => fall back to subtotal. */
  totalCents: number | undefined;
  /** True when a coupon is active. Shows the "{X}% OFF APPLIED" chip + strikethrough. */
  isCouponApplied: boolean;
  /** Integer percent (e.g. 35). Only rendered when isCouponApplied. */
  discountPercent: number;
  /** Form is valid + ready to submit. Drives button color/disabled state. */
  isEnabled: boolean;
  /** Form is submitting. Renders spinner and disables button. */
  isSubmitting: boolean;
  /** Click handler. Same pattern as the previous StickyMobileCTA. */
  onClick: () => void;
  /**
   * Sticky variant only: mount/unmount based on whether the inline submit
   * button is in viewport. Ignored when `variant === 'inline'` (inline is
   * always visible).
   */
  isVisible?: boolean;
  /** Submit button label (e.g. "Complete payment" / "Start my risk assessment"). */
  submitLabel: string;
  /**
   * Initial expand state for the breakdown panel. Defaults to false. Mostly
   * used by Storybook to render the open variant deterministically; runtime
   * users tap the header to toggle.
   */
  defaultExpanded?: boolean;
  /**
   * Layout variant. `sticky` (default) fixes the bar to viewport bottom on
   * mobile. `inline` renders in normal flow inside the payment card so
   * desktop users get the same expanding-breakdown treatment.
   */
  variant?: 'sticky' | 'inline';
  /**
   * Submit form id. When provided, the submit button gets `type="submit"`
   * and `form={formSubmitId}` so it triggers the named form. Otherwise the
   * button is a plain `type="button"` and relies on `onClick`. The inline
   * variant typically passes the parent form id; the sticky variant uses
   * `onClick` to programmatically click the inline submit.
   */
  formSubmitId?: string;
  /**
   * Optional handler invoked when the "Enter Zipcode" placeholder is
   * clicked (only rendered when tax hasn't been computed yet). Parent
   * should focus + scroll into view the zip input so the user can
   * immediately complete it.
   */
  onClickEnterZipcode?: () => void;
  /**
   * True while a price refetch is in flight for a just-entered zip. During this
   * window the tax/total are stale (still the zip-less values), so we show a
   * "Calculating…" state instead of briefly displaying a wrong $0 tax / untaxed
   * total before the real figures land.
   */
  isCalculatingTax?: boolean;
}

/** Format an integer cent amount as USD ($199.00 / $216.41). */
function formatUSD(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

export function StickyPricingCTA({
  subtotalCents,
  recurringCents,
  taxCents,
  totalCents,
  isCouponApplied,
  discountPercent,
  isEnabled,
  isSubmitting,
  onClick,
  isVisible = true,
  submitLabel,
  defaultExpanded = false,
  variant = 'sticky',
  formSubmitId,
  onClickEnterZipcode,
  isCalculatingTax = false,
}: StickyPricingCTAProps) {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  // Sticky variant: mount/unmount based on scroll visibility. Inline is always rendered.
  if (variant === 'sticky' && !isVisible) {
    return null;
  }

  // Header label flips once tax is known (post-zip). While a refetch is in
  // flight the tax/total are stale, so keep the pre-tax "Subtotal" framing
  // rather than flashing "Total due today" with the wrong number.
  const hasTax = taxCents !== undefined && !isCalculatingTax;
  const headerLabel = hasTax ? 'Total due today' : 'Subtotal';

  // Right-side header price: prefer total, fall back to subtotal. During tax
  // calculation, show the subtotal (not the stale, untaxed total).
  const headerPriceCents = isCalculatingTax ? (subtotalCents ?? 0) : (totalCents ?? subtotalCents ?? 0);

  // Membership-line numbers. When the coupon discount is active we render
  // the subtotal struck through with the recurring amount next to it.
  // Matches the Lovable prototype which strikes the subtotal whenever a
  // promo is applied (no "discount > 0" check — the chip itself communicates
  // the discount status).
  const showStrikethrough =
    isCouponApplied &&
    typeof subtotalCents === 'number' &&
    typeof recurringCents === 'number';

  const isSticky = variant === 'sticky';

  // Outer wrapper differs by variant. Sticky overlays the viewport bottom on
  // mobile; inline sits in normal flow (visible on all sizes).
  const outerClassName = isSticky
    ? 'fixed bottom-0 left-0 right-0 z-50 border-t border-solid p-4 backdrop-blur-sm pb-safe-offset-4 md:hidden'
    : 'w-full';
  const outerStyle: React.CSSProperties = isSticky
    ? { backgroundColor: 'rgba(246,242,234,0.96)', borderColor: '#E3DCCE' }
    : {};

  return (
    <div className={outerClassName} style={outerStyle}>
      <div
        className={
          isSticky
            ? 'mx-auto flex w-full max-w-lg flex-col gap-3'
            : 'flex w-full flex-col gap-3'
        }
      >
        {/* Header row — tappable to toggle the breakdown */}
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-controls="sticky-price-breakdown"
          className="flex min-h-[44px] w-full items-center justify-between rounded-lg px-1 text-left"
        >
          <span className="text-sm font-semibold" style={{ color: '#8A857B' }}>{headerLabel}</span>
          <span className="flex items-center gap-2">
            <span className="text-lg font-extrabold" style={{ color: '#0E1D27' }}>
              {formatUSD(headerPriceCents)}
            </span>
            <ChevronDown
              aria-hidden="true"
              className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              style={{ color: '#0E1D27' }}
            />
          </span>
        </button>

        {/* Expanded panel — breakdown */}
        {isOpen && (
          <div
            id="sticky-price-breakdown"
            role="region"
            aria-label="Price breakdown"
            className="flex flex-col gap-2 rounded-lg border px-3 py-2.5 text-sm"
            style={{ backgroundColor: 'rgba(255,255,255,0.7)', borderColor: '#E3DCCE' }}
          >
            {/* Catch Membership row */}
            <div className="flex items-center justify-between gap-3">
              <span className="flex flex-wrap items-center gap-2 font-medium" style={{ color: '#8A857B' }}>
                Catch Membership
                {isCouponApplied && (
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                    style={{
                      backgroundColor: 'rgba(124,241,199,0.35)',
                      color: '#0a8a5f',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {discountPercent}% OFF APPLIED
                  </span>
                )}
              </span>
              <span className="flex items-baseline gap-1.5 font-semibold" style={{ color: '#0E1D27' }}>
                {showStrikethrough && typeof subtotalCents === 'number' && typeof recurringCents === 'number' ? (
                  <>
                    <s className="font-medium" style={{ color: '#8A857B' }}>{formatUSD(subtotalCents)}</s>
                    <span>{formatUSD(recurringCents)}</span>
                  </>
                ) : (
                  <span>{typeof subtotalCents === 'number' ? formatUSD(subtotalCents) : '—'}</span>
                )}
              </span>
            </div>

            {/* Estimated tax row */}
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium" style={{ color: '#8A857B' }}>Estimated tax</span>
              <span>
                {isCalculatingTax ? (
                  <span className="font-medium" style={{ color: '#8A857B' }}>Calculating…</span>
                ) : typeof taxCents === 'number' ? (
                  <span className="font-semibold" style={{ color: '#0E1D27' }}>{formatUSD(taxCents)}</span>
                ) : onClickEnterZipcode ? (
                  <button
                    type="button"
                    onClick={onClickEnterZipcode}
                    className="font-semibold underline-offset-2 hover:underline focus:underline focus:outline-none"
                    style={{ color: '#0E1D27' }}
                  >
                    Enter Zipcode
                  </button>
                ) : (
                  <span className="font-semibold" style={{ color: '#0E1D27' }}>Enter Zipcode</span>
                )}
              </span>
            </div>

            <div className="h-px" style={{ backgroundColor: '#E3DCCE' }} />

            {/* Total row */}
            <div className="flex items-center justify-between gap-3">
              <span className="font-bold" style={{ color: '#0E1D27' }}>Total</span>
              <span className="font-extrabold" style={{ color: '#0E1D27' }}>
                {isCalculatingTax ? 'Calculating…' : formatUSD(totalCents ?? subtotalCents ?? 0)}
              </span>
            </div>
          </div>
        )}

        {/* Submit button. Inline variant submits the parent form natively;
            sticky variant relies on onClick (typically programmatic-clicks
            the inline submit so a single submit handler runs). */}
        <button
          type={formSubmitId ? 'submit' : 'button'}
          form={formSubmitId}
          onClick={formSubmitId ? undefined : onClick}
          disabled={isSubmitting}
          className={`flex min-h-[56px] w-full items-center justify-center rounded-full px-6 text-base font-semibold transition-all ${
            isEnabled
              ? 'bg-catch-light-blue text-catch-dark-blue-90 hover:brightness-95'
              : 'bg-slate-200 text-slate-500'
          } disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {isSubmitting ? (
            <span className="inline-flex items-center gap-2">
              <svg
                className="h-5 w-5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Processing...
            </span>
          ) : (
            submitLabel
          )}
        </button>
      </div>
    </div>
  );
}
